<script setup lang="ts">
import { useSerialize } from "@lxc/app-device-common";
import type { Rules } from "async-validator";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useAcl } from "vue-simple-acl";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import type { EntityLocation } from "~/core/models/EntityLocation.interface";
import LxcError from "~/core/models/LxcError.class";
import { getEntityLocationValidationRules } from "~/core/utils/entityLocationValidationRules";
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import { typeOptions } from "~/modules/organization/constants";
import { OrganizationType } from "~/modules/organization/models/OrganizationType.enum";
import type { OrganizationTypeOption } from "~/modules/organization/models/OrganizationTypeOption.interface";
import type { OrganizationUpdate } from "~/modules/organization/models/OrganizationUpdate.type";
import type { OrganizationV2 } from "~/modules/organization/models/OrganizationV2.interface";
import organizationService from "~/modules/organization/services/organization.service";
import { useConfirmLeavePageStore } from "~/stores/useConfirmLeavePageStore";

interface Props {
  modelValue: OrganizationV2;
}
const props = defineProps<Props>();

const { t } = useI18n();
const confirmLeavePageStore = useConfirmLeavePageStore();
const serialize = useSerialize();
const { can } = useAcl();

const name: Ref<string> = ref(props.modelValue?.name);
const currentType: OrganizationTypeOption | undefined = typeOptions.value.find(
  (option) => {
    return option.key === props.modelValue?.type;
  },
);
const type: Ref<string> = ref(
  currentType ? t(currentType.labelKey) : props.modelValue?.type,
);
const location: Ref<EntityLocation> = ref({
  country: props.modelValue?.country,
  timezone: props.modelValue?.timezone,
  latitude: props.modelValue?.latitude,
  longitude: props.modelValue?.longitude,
});
let initLocationFormStringified: Ref<string> = ref(serialize(location.value));
const formHasChanged: ComputedRef<boolean> = computed(
  () => serialize(location.value) !== initLocationFormStringified.value,
);
const isLoading: Ref<boolean> = ref(false);
const buttonSaveLabel: string = t("button.save");

const formRef: Ref<HTMLFormElement | undefined | null> = ref();
const formRules: Rules = getEntityLocationValidationRules(t);
const isFormValid: ComputedRef<boolean | undefined> = computed(
  () => formRef.value?.isValid,
);

watch(
  () => formHasChanged.value,
  (hasChanged: boolean) => {
    confirmLeavePageStore.activate(hasChanged);
  },
);

async function onFormSubmit() {
  if (isFormValid.value) {
    isLoading.value = true;

    const organizationRequest: OrganizationUpdate = {
      ...location.value,
      name: name.value,
      type: props.modelValue?.type,
      code: props.modelValue?.code,
    };

    const response = await organizationService.updateOrganization(
      props.modelValue?.code,
      organizationRequest,
    );

    if (LxcError.check(response)) {
      response.notify(NotificationKey.saveError);
    } else {
      showNotificationSuccess(t(NotificationKey.saveSuccess));
      initLocationFormStringified.value = serialize(location.value);
    }

    isLoading.value = false;
  }
}
</script>

<template>
  <lxc-form
    ref="formRef"
    :rules="formRules"
    :model="location"
    @submit.prevent="onFormSubmit"
  >
    <div class="mb-6 flex justify-between">
      <h4 class="text-gray-900">
        {{ t("administration.organization.settings.description.title") }}
      </h4>
      <export-id-card-button
        v-if="modelValue.type === OrganizationType.ADMINISTRATION"
        :organization-code="modelValue.code"
      />
    </div>
    <div class="md:flex gap-x-8 lg:w-[calc(50%-32px)]">
      <lxc-form-item
        class="md:basis-1/2"
        :label="t('administration.organization.name')"
        disabled
      >
        <lxc-input v-model="name" type="text" />
      </lxc-form-item>
      <lxc-form-item
        class="md:basis-1/2"
        :label="t('administration.organization.type.label')"
        disabled
      >
        <lxc-input v-model="type" type="text" />
      </lxc-form-item>
    </div>

    <hr class="my-8 max-w-[75%] text-gray-200" />
    <entity-location
      v-model="location"
      :disabled="!can(ACLRoles.CAN_WRITE_ORG)"
    ></entity-location>
    <lxc-button
      class="mt-4"
      type="primary"
      :is-loading="isLoading"
      :disabled="!formHasChanged || !isFormValid"
      :title="buttonSaveLabel"
      :text="buttonSaveLabel"
      html-type="submit"
    />
  </lxc-form>
</template>
